import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Tooltip as MuiTooltip,
  Box as MuiBox,
  useTheme as useMuiTheme,
  useMediaQuery
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import {
  ChakraProvider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  IconButton,
  Icon,
  Badge,
  Alert,
  AlertIcon,
  Flex,
  Divider,
  Spinner,
  Tooltip as ChakraTooltip,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb
} from '@chakra-ui/react';
import {
  FiMic,
  FiSend,
  FiVolume2,
  FiVolumeX,
  FiRotateCcw,
  FiPlayCircle,
  FiArrowRight,
  FiRefreshCw,
  FiInfo,
  FiSquare,
  FiRepeat,
  FiSettings,
  FiSliders,
  FiArrowLeft,
  FiCheck,
  FiX
} from 'react-icons/fi';
import { FaRobot } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useVoiceAssistant } from '../../hooks/useVoiceAssistant';

const MotionBox = motion(Box);

// ─────────────────────────────────────────────────────────
// VOICE ORB
// ─────────────────────────────────────────────────────────
const VoiceOrb = ({ status, mediaStream }) => {
  const orbRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!mediaStream || status !== 'listening') {
      cancelAnimationFrame(rafRef.current);
      if (orbRef.current) {
        orbRef.current.style.transform = 'scale(1)';
        orbRef.current.style.boxShadow =
          'inset -10px -10px 40px rgba(0,0,0,0.15), inset 10px 10px 40px rgba(255,255,255,0.4), 0 10px 30px rgba(107,70,193,0.3)';
      }
      return;
    }

    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    } else if (audioCtxRef.current.state === 'suspended') {
      void audioCtxRef.current.resume();
    }

    const ctx = audioCtxRef.current;
    if (!analyserRef.current) {
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.85;
      analyserRef.current = analyser;
    }

    const analyser = analyserRef.current;
    try {
      if (sourceRef.current) sourceRef.current.disconnect();
      const source = ctx.createMediaStreamSource(mediaStream);
      source.connect(analyser);
      sourceRef.current = source;
    } catch (e) {}

    const data = new Uint8Array(analyser.frequencyBinCount);
    const animate = () => {
      analyser.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      const n = Math.min(1, avg / 128);
      if (orbRef.current) {
        orbRef.current.style.transform = `scale(${1 + n * 0.14})`;
        orbRef.current.style.boxShadow = `inset -10px -10px 40px rgba(0,0,0,0.15), inset 10px 10px 40px rgba(255,255,255,0.45), 0 12px ${30 + n * 50}px rgba(107,70,193,${0.4 + n * 0.5})`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      try { sourceRef.current?.disconnect(); } catch (_) {}
    };
  }, [mediaStream, status]);

  let gradient =
    'radial-gradient(circle at 30% 30%, #ffffff 0%, #b3f0ff 15%, #a78bfa 45%, #6b46c1 70%, #319795 100%)';
  let animation = 'orbFloat 6s ease-in-out infinite';

  if (status === 'error') {
    gradient = 'radial-gradient(circle at 30% 30%, #ffffff 0%, #ffc3a0 15%, #ffafbd 45%, #c94b4b 70%, #4b134f 100%)';
  } else if (status === 'processing' || status === 'responding') {
    animation = 'orbFloat 4s ease-in-out infinite, orbPulse 1.5s infinite alternate';
  } else if (status === 'listening') {
    animation = 'orbFloat 3s ease-in-out infinite';
  }

  return (
    <Flex align="center" justify="center" py={3} w="full" position="relative">
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <filter id="orb-turbulence">
          <feTurbulence type="fractalNoise" baseFrequency={status === 'listening' ? '0.015' : '0.01'} numOctaves="3" result="noise">
            <animate attributeName="baseFrequency" values="0.01;0.015;0.01" dur="12s" repeatCount="indefinite" />
          </feTurbulence>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale={status === 'listening' ? '18' : '10'} xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </svg>
      <Box
        ref={orbRef}
        w={{ base: '110px', md: '140px' }}
        h={{ base: '110px', md: '140px' }}
        borderRadius="full"
        sx={{
          background: gradient,
          animation,
          filter: 'url(#orb-turbulence)',
          transition: 'transform 0.1s ease-out, box-shadow 0.1s ease-out, background 1s ease',
          boxShadow: 'inset -10px -10px 40px rgba(0,0,0,0.15), inset 10px 10px 40px rgba(255,255,255,0.4), 0 10px 30px rgba(107,70,193,0.3)',
          '@keyframes orbFloat': {
            '0%, 100%': { transform: 'translateY(0)' },
            '50%': { transform: 'translateY(-10px)' },
          },
          '@keyframes orbPulse': {
            '0%': { filter: 'url(#orb-turbulence) brightness(0.95)' },
            '100%': { filter: 'url(#orb-turbulence) brightness(1.2)' },
          },
        }}
      />
    </Flex>
  );
};

// ─────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────
export const GlobalVoiceAssistant = () => {
  const a = useVoiceAssistant();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isDraggingRef = useRef(false);
  const [inputText, setInputText] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Sync route change and auto-close
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      a.closeAssistant(true); // close modal, keep speaking
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, a]);

  // Reset settings panel when assistant closes
  useEffect(() => {
    if (!a.isOpen) {
      setShowSettings(false);
    }
  }, [a.isOpen]);

  // Scroll to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [a.transcript, a.response, a.status, a.pendingProduct]);

  const handleSendText = async () => {
    const text = inputText.trim();
    if (!text) return;
    setInputText('');
    await a.runCommand(text);
  };

  const handleSuggestionClick = async (suggestion) => {
    await a.runCommand(suggestion);
  };

  const isMicDisabled =
    a.status === 'processing' ||
    a.status === 'responding' ||
    a.isSpeaking;

  const statusLabel = {
    idle: 'En línea',
    listening: 'Escuchando…',
    processing: 'Procesando…',
    responding: 'Hablando…',
    error: 'Modo texto',
  }[a.status] || 'En línea';

  const statusColor = {
    idle: 'green.400',
    listening: 'red.400',
    processing: 'yellow.400',
    responding: 'teal.400',
    error: 'gray.400',
  }[a.status] || 'purple.400';

  return (
    <>
      {/* Botón Flotante Rediseñado de Micrófono IA (Draggable, Elevado y con Efecto Neón/Ondas) */}
      {!a.isOpen && (
        <motion.div
          drag
          dragMomentum={false}
          dragElastic={0.08}
          onDragStart={() => { isDraggingRef.current = true; }}
          onDragEnd={() => { setTimeout(() => { isDraggingRef.current = false; }, 120); }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.93 }}
          style={{
            position: 'fixed',
            right: isMobile ? 18 : 24,
            bottom: isMobile ? 90 : 96,
            zIndex: 1050, // Permite que los Drawers y Modales (zIndex 1200+) queden por encima sin ser tapados
            touchAction: 'none',
            cursor: 'grab',
          }}
        >
          <MuiTooltip
            title="🎙 Asistente IA de Voz (Toca para hablar • Arrastra para reubicar)"
            placement="left"
            arrow
          >
            <MuiBox
              role="button"
              aria-label="voice-assistant"
              onClick={() => {
                if (isDraggingRef.current) return;
                a.openAssistant({ startListening: true });
              }}
              sx={{
                position: 'relative',
                width: { xs: 52, md: 56 },
                height: { xs: 52, md: 56 },
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: a.status === 'listening'
                  ? 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #06b6d4 100%)'
                  : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #06b6d4 100%)',
                boxShadow: a.status === 'listening'
                  ? '0 12px 36px rgba(236, 72, 153, 0.55), 0 4px 14px rgba(6, 182, 212, 0.4), inset 0 1.5px 1.5px rgba(255,255,255,0.6)'
                  : '0 10px 30px rgba(99, 102, 241, 0.42), 0 4px 12px rgba(6, 182, 212, 0.32), inset 0 1.5px 1.5px rgba(255,255,255,0.5)',
                border: '2px solid rgba(255, 255, 255, 0.35)',
                backdropFilter: 'blur(12px)',
                userSelect: 'none',
                transition: 'background 0.35s ease, box-shadow 0.35s ease',
                animation: a.status === 'listening' ? 'none' : 'aiAmbientPulse 4s ease-in-out infinite',
                '@keyframes aiAmbientPulse': {
                  '0%, 100%': { filter: 'drop-shadow(0 4px 12px rgba(124, 58, 237, 0.35))' },
                  '50%': { filter: 'drop-shadow(0 6px 20px rgba(6, 182, 212, 0.6))' }
                },
                '@keyframes waveBarAnim': {
                  '0%': { height: '6px' },
                  '100%': { height: '22px' }
                },
                '&:hover': {
                  boxShadow: '0 14px 42px rgba(99, 102, 241, 0.6), 0 6px 18px rgba(6, 182, 212, 0.5)',
                },
                '&:active': {
                  cursor: 'grabbing',
                }
              }}
            >
              {/* Anillos sónicos concéntricos al escuchar */}
              {a.status === 'listening' && (
                <>
                  <MuiBox
                    sx={{
                      position: 'absolute',
                      inset: -4,
                      borderRadius: '50%',
                      border: '2px solid #ec4899',
                      animation: 'sonicPulse1 1.4s ease-out infinite',
                      pointerEvents: 'none',
                      '@keyframes sonicPulse1': {
                        '0%': { transform: 'scale(1)', opacity: 0.8 },
                        '100%': { transform: 'scale(1.7)', opacity: 0 }
                      }
                    }}
                  />
                  <MuiBox
                    sx={{
                      position: 'absolute',
                      inset: -4,
                      borderRadius: '50%',
                      border: '2px solid #06b6d4',
                      animation: 'sonicPulse2 1.4s ease-out infinite 0.45s',
                      pointerEvents: 'none',
                      '@keyframes sonicPulse2': {
                        '0%': { transform: 'scale(1)', opacity: 0.7 },
                        '100%': { transform: 'scale(1.7)', opacity: 0 }
                      }
                    }}
                  />
                </>
              )}

              {/* Micro badge "AI" estilo neón */}
              <MuiBox
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                  color: '#ffffff',
                  fontSize: '9px',
                  fontWeight: 900,
                  letterSpacing: '0.5px',
                  px: '5px',
                  py: '1px',
                  borderRadius: '10px',
                  border: '1.5px solid #ffffff',
                  boxShadow: '0 2px 8px rgba(6, 182, 212, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  pointerEvents: 'none',
                }}
              >
                AI
              </MuiBox>

              {/* Ícono de Micrófono o Barras animadas de sonido */}
              {a.status === 'listening' ? (
                <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 22 }}>
                  <MuiBox sx={{ width: 3, height: 12, bgcolor: '#ffffff', borderRadius: 1, animation: 'waveBarAnim 0.5s ease-in-out infinite alternate' }} />
                  <MuiBox sx={{ width: 3, height: 20, bgcolor: '#ffffff', borderRadius: 1, animation: 'waveBarAnim 0.7s ease-in-out infinite alternate 0.15s' }} />
                  <MuiBox sx={{ width: 3, height: 16, bgcolor: '#ffffff', borderRadius: 1, animation: 'waveBarAnim 0.45s ease-in-out infinite alternate 0.3s' }} />
                  <MuiBox sx={{ width: 3, height: 10, bgcolor: '#ffffff', borderRadius: 1, animation: 'waveBarAnim 0.65s ease-in-out infinite alternate 0.1s' }} />
                </MuiBox>
              ) : (
                <MicIcon
                  sx={{
                    color: '#ffffff',
                    fontSize: { xs: 24, md: 26 },
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))'
                  }}
                />
              )}
            </MuiBox>
          </MuiTooltip>
        </motion.div>
      )}

      {/* Glassmorphic Assistant Modal (Chakra UI) */}
      {a.isOpen && (
        <ChakraProvider resetCSS={false}>
          <Modal
            isOpen={a.isOpen}
            onClose={a.closeAssistant}
            isCentered
            size={{ base: 'full', md: 'lg' }}
            motionPreset="slideInBottom"
          >
            <ModalOverlay bg="blackAlpha.850" backdropFilter="blur(20px)" />
            
            <ModalContent
              bg="rgba(15, 10, 26, 0.88)"
              backdropFilter="blur(30px)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.08)"
              borderRadius={{ base: 0, md: '32px' }}
              overflow="hidden"
              boxShadow="0 25px 60px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.15)"
              mx={{ base: 0, md: 4 }}
              h={{ base: '100%', md: '620px' }}
              maxH="100dvh"
              display="flex"
              flexDirection="column"
              position="relative"
              color="white"
            >
              {/* Estilos CSS Globales Inyectados para Animaciones */}
              <style dangerouslySetInnerHTML={{__html: `
                @keyframes avatarPulse {
                  0% { transform: scale(0.96); box-shadow: 0 0 0 0 rgba(107, 70, 193, 0.5); }
                  70% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(107, 70, 193, 0); }
                  100% { transform: scale(0.96); box-shadow: 0 0 0 0 rgba(107, 70, 193, 0); }
                }
                @keyframes onlinePulse {
                  0%, 100% { opacity: 0.65; transform: scale(0.9); }
                  50% { opacity: 1; transform: scale(1.15); }
                }
                @keyframes nebulaGlow {
                  0% { transform: translate(0px, 0px) scale(1); }
                  33% { transform: translate(20px, -30px) scale(1.08); }
                  66% { transform: translate(-15px, 15px) scale(0.95); }
                  100% { transform: translate(0px, 0px) scale(1); }
                }
                .custom-scrollbar::-webkit-scrollbar {
                  width: 5px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: rgba(255, 255, 255, 0.02);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background: rgba(107, 70, 193, 0.3);
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: rgba(49, 151, 149, 0.5);
                }
              `}} />

              {/* Nebulosas del Fondo */}
              <Box
                position="absolute"
                top="-10%"
                left="-10%"
                w="260px"
                h="260px"
                borderRadius="full"
                bg="radial-gradient(circle, rgba(107, 70, 193, 0.16) 0%, rgba(107, 70, 193, 0) 70%)"
                filter="blur(45px)"
                pointerEvents="none"
                zIndex={0}
                style={{ animation: 'nebulaGlow 12s infinite alternate' }}
              />
              <Box
                position="absolute"
                bottom="-5%"
                right="-5%"
                w="220px"
                h="220px"
                borderRadius="full"
                bg="radial-gradient(circle, rgba(49, 151, 149, 0.12) 0%, rgba(49, 151, 149, 0) 70%)"
                filter="blur(40px)"
                pointerEvents="none"
                zIndex={0}
                style={{ animation: 'nebulaGlow 10s infinite alternate-reverse' }}
              />

              {/* Header */}
              <ModalHeader
                bg="rgba(255, 255, 255, 0.03)"
                borderBottom="1px solid"
                borderColor="rgba(255, 255, 255, 0.06)"
                color="white"
                px={5}
                py={4}
                position="relative"
                flexShrink={0}
                zIndex={1}
              >
                <Flex align="center" gap={3}>
                  <Box position="relative">
                    <Flex
                      w="42px"
                      h="42px"
                      borderRadius="full"
                      bg="linear-gradient(135deg, #6b46c1, #319795)"
                      align="center"
                      justify="center"
                      boxShadow="0 4px 15px rgba(107, 70, 193, 0.4)"
                      style={{ animation: 'avatarPulse 2.5s infinite' }}
                    >
                      <Icon as={FaRobot} color="white" boxSize={5} />
                    </Flex>
                    <Box
                      position="absolute"
                      bottom="0"
                      right="0"
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={statusColor}
                      border="2px solid"
                      borderColor="rgba(15, 10, 26, 1)"
                      style={{ animation: 'onlinePulse 1.8s infinite' }}
                    />
                  </Box>

                  <Box flex={1} minW={0}>
                    <Text fontSize="md" fontWeight="800" letterSpacing="-0.3px">
                      Asistente Posent IA
                    </Text>
                    <HStack mt={0.5} spacing={2}>
                      <Badge
                        bg="rgba(255, 255, 255, 0.08)"
                        color="whiteAlpha.900"
                        borderRadius="full"
                        px={2.5}
                        py={0.2}
                        fontSize="2xs"
                        fontWeight="700"
                      >
                        {statusLabel}
                      </Badge>
                    </HStack>
                  </Box>

                  {/* Settings toggle */}
                  <ChakraTooltip label={showSettings ? "Volver al asistente" : "Ajustes de voz"} placement="bottom" bg="gray.800" color="white" borderRadius="8px" fontSize="xs">
                    <IconButton
                      aria-label="Ajustes de voz"
                      icon={showSettings ? <FiArrowLeft /> : <FiSettings />}
                      size="sm"
                      variant="ghost"
                      color="whiteAlpha.600"
                      _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                      borderRadius="full"
                      transition="all 0.25s"
                      onClick={() => setShowSettings(!showSettings)}
                    />
                  </ChakraTooltip>

                  {/* Close button */}
                  <ChakraTooltip label="Cerrar asistente" placement="bottom" bg="gray.800" color="white" borderRadius="8px" fontSize="xs">
                    <IconButton
                      aria-label="Cerrar asistente"
                      icon={<FiX />}
                      size="sm"
                      variant="ghost"
                      color="whiteAlpha.600"
                      _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                      borderRadius="full"
                      transition="all 0.25s"
                      onClick={a.closeAssistant}
                    />
                  </ChakraTooltip>
                </Flex>
              </ModalHeader>

              {/* Modal Body */}
              <ModalBody p={0} flex={1} display="flex" flexDirection="column" overflow="hidden" zIndex={1}>
                <AnimatePresence mode="wait">
                  {!showSettings ? (
                    <MotionBox
                      key="assistant-view"
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -15 }}
                      transition={{ duration: 0.25 }}
                      display="flex"
                      flexDirection="column"
                      flex={1}
                      overflow="hidden"
                    >
                      <Box
                        flex={1}
                        overflowY="auto"
                        px={4}
                        py={5}
                        className="custom-scrollbar"
                      >
                        <VStack spacing={4} align="stretch">
                          {/* Welcome message */}
                          <MotionBox
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}
                          >
                            <Flex gap={3} align="flex-start">
                              <Flex
                                w="32px"
                                h="32px"
                                borderRadius="full"
                                bg="rgba(107, 70, 193, 0.2)"
                                border="1px solid"
                                borderColor="rgba(107, 70, 193, 0.4)"
                                align="center"
                                justify="center"
                                flexShrink={0}
                              >
                                <Icon as={FaRobot} color="purple.300" boxSize={3.5} />
                              </Flex>
                              <Box
                                bg="rgba(255, 255, 255, 0.05)"
                                border="1px solid"
                                borderColor="rgba(255, 255, 255, 0.06)"
                                px={4}
                                py={3}
                                borderRadius="4px 18px 18px 18px"
                                maxW="85%"
                              >
                                <Text fontSize="sm" color="whiteAlpha.950" lineHeight="1.6">
                                  Hola. Soy el asistente inteligente de <strong>POSENT</strong>. Puedo ayudarte a buscar productos, agregarlos al carrito de ventas, consultar inventarios, realizar cortes de caja y navegar en los diferentes módulos del punto de venta. ¿En qué te ayudo hoy? 🛒🎙️
                                </Text>
                              </Box>
                            </Flex>
                          </MotionBox>

                          {/* Last user transcript */}
                          {a.transcript && (
                            <MotionBox
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                            >
                              <Flex gap={3} align="flex-start" justify="flex-end">
                                <Box
                                  bg="linear-gradient(135deg, #6b46c1, #319795)"
                                  px={4}
                                  py={3}
                                  borderRadius="18px 4px 18px 18px"
                                  maxW="80%"
                                  boxShadow="0 4px 12px rgba(107,70,193,0.25)"
                                >
                                  <Text fontSize="sm" fontWeight="500" color="white">
                                    {a.transcript}
                                  </Text>
                                </Box>
                              </Flex>
                            </MotionBox>
                          )}

                          {/* Last AI Response */}
                          {a.response && (
                            <MotionBox
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                            >
                              <Flex gap={3} align="flex-start">
                                <Flex
                                  w="32px"
                                  h="32px"
                                  borderRadius="full"
                                  bg="rgba(107, 70, 193, 0.2)"
                                  border="1px solid"
                                  borderColor="rgba(107, 70, 193, 0.4)"
                                  align="center"
                                  justify="center"
                                  flexShrink={0}
                                >
                                  <Icon as={FaRobot} color="teal.300" boxSize={3.5} />
                                </Flex>
                                <Box
                                  bg="rgba(255, 255, 255, 0.05)"
                                  border="1px solid"
                                  borderColor="rgba(255, 255, 255, 0.06)"
                                  px={4}
                                  py={3}
                                  borderRadius="4px 18px 18px 18px"
                                  maxW="85%"
                                >
                                  <Text fontSize="sm" color="whiteAlpha.950" lineHeight="1.6" whiteSpace="pre-wrap">
                                    {a.response}
                                  </Text>
                                </Box>
                              </Flex>
                            </MotionBox>
                          )}

                          {/* Product confirmation card */}
                          <AnimatePresence>
                            {a.pendingProduct && (
                              <MotionBox
                                initial={{ opacity: 0, scale: 0.96, y: 15 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.4, type: 'spring', stiffness: 100 }}
                                bg="rgba(237, 137, 54, 0.12)"
                                border="1px solid"
                                borderColor="orange.400"
                                borderRadius="20px"
                                p={4}
                                w="full"
                              >
                                <Text fontSize="2xs" color="orange.300" fontWeight="800" textTransform="uppercase" mb={1}>
                                  Confirmación de Pre-Orden 🛒
                                </Text>
                                <Text fontSize="sm" fontWeight="800" color="white">
                                  {a.pendingProduct.nombre || a.pendingProduct.name}
                                </Text>
                                <Text fontSize="xs" color="whiteAlpha.700" mb={3}>
                                  RD$ {a.pendingProduct.precio || a.pendingProduct.price} · Stock: {a.pendingProduct.stock ?? '–'}
                                </Text>
                                <HStack justify="flex-end" spacing={3}>
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    color="whiteAlpha.800"
                                    onClick={a.cancelPendingProduct}
                                    _hover={{ bg: 'whiteAlpha.100' }}
                                  >
                                    No, cancelar
                                  </Button>
                                  <Button
                                    size="xs"
                                    colorScheme="orange"
                                    bg="orange.500"
                                    color="white"
                                    onClick={a.confirmPendingProduct}
                                    _hover={{ bg: 'orange.600' }}
                                    boxShadow="0 4px 12px rgba(237,137,54,0.3)"
                                  >
                                    Sí, agregar
                                  </Button>
                                </HStack>
                              </MotionBox>
                            )}
                          </AnimatePresence>

                          {/* Thinking Spinner */}
                          {a.status === 'processing' && (
                            <Flex gap={3} align="center" px={1}>
                              <Spinner size="xs" color="teal.300" thickness="1.5px" />
                              <Text fontSize="xs" color="whiteAlpha.600">Procesando comando...</Text>
                            </Flex>
                          )}
                        </VStack>
                        <div ref={chatEndRef} />
                      </Box>

                      {/* Controls Footer */}
                      <Box
                        p={4}
                        bg="rgba(255, 255, 255, 0.02)"
                        borderTop="1px solid"
                        borderColor="rgba(255, 255, 255, 0.05)"
                        flexShrink={0}
                      >
                        <VStack spacing={4} align="stretch">
                          {/* Orb display during listening/responding */}
                          <AnimatePresence>
                            {(a.status === 'listening' || a.status === 'responding') && (
                              <MotionBox
                                key="orb-area"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.35 }}
                                overflow="hidden"
                              >
                                <VStack spacing={2} pb={2}>
                                  <VoiceOrb status={a.status} mediaStream={a.mediaStream} />
                                  {a.status === 'listening' && (
                                    <Text fontSize="xs" color="purple.300" fontWeight="800" textAlign="center">
                                      Te escucho... habla ahora
                                    </Text>
                                  )}
                                </VStack>
                              </MotionBox>
                            )}
                          </AnimatePresence>

                          {/* Error block */}
                          <AnimatePresence>
                            {a.error && (
                              <MotionBox
                                key="error-block"
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                              >
                                <Alert bg="rgba(229, 62, 62, 0.15)" border="1px solid" borderColor="red.400" color="white" borderRadius="14px" fontSize="xs" py={2.5}>
                                  <AlertIcon color="red.300" />
                                  <Text whiteSpace="pre-wrap">{a.error}</Text>
                                </Alert>
                              </MotionBox>
                            )}
                          </AnimatePresence>

                          {/* Quick suggestions */}
                          {a.status === 'idle' && (
                            <Flex wrap="wrap" gap={2}>
                              {['Ventas de hoy', 'Stock bajo', 'Mis productos', 'Abrir caja', 'Configuración'].map((chip) => (
                                <Button
                                  key={chip}
                                  size="xs"
                                  variant="outline"
                                  borderColor="rgba(107,70,193,0.3)"
                                  color="purple.200"
                                  bg="rgba(107, 70, 193, 0.05)"
                                  borderRadius="full"
                                  onClick={() => handleSuggestionClick(chip)}
                                  _hover={{ bg: 'rgba(107,70,193,0.15)', color: 'white' }}
                                >
                                  {chip}
                                </Button>
                              ))}
                            </Flex>
                          )}

                          {/* Input row */}
                          <VStack spacing={3} align="stretch">
                            <HStack spacing={2}>
                              <Input
                                ref={inputRef}
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    void handleSendText();
                                  }
                                }}
                                placeholder="Escribe un comando o habla..."
                                borderRadius="16px"
                                h="44px"
                                bg="rgba(255, 255, 255, 0.04)"
                                border="1px solid"
                                borderColor="rgba(255, 255, 255, 0.08)"
                                color="white"
                                _placeholder={{ color: 'whiteAlpha.400' }}
                                _focus={{ borderColor: 'teal.400', boxShadow: '0 0 10px rgba(49, 151, 149, 0.25)' }}
                                fontSize="sm"
                              />

                              <ChakraTooltip label="Enviar pregunta" placement="top" bg="gray.850" color="white" borderRadius="8px" fontSize="xs">
                                <IconButton
                                  aria-label="Enviar pregunta"
                                  icon={<FiSend />}
                                  onClick={handleSendText}
                                  isDisabled={!inputText.trim()}
                                  colorScheme="purple"
                                  bgGradient="linear(to-r, #6b46c1, #319795)"
                                  borderRadius="16px"
                                  h="44px"
                                  w="46px"
                                />
                              </ChakraTooltip>

                              <ChakraTooltip label={a.status === 'listening' ? 'Detener micrófono' : 'Hablar'} placement="top" bg="gray.850" color="white" borderRadius="8px" fontSize="xs">
                                <IconButton
                                  aria-label="Micrófono"
                                  icon={a.status === 'listening' ? <FiSquare /> : <FiMic />}
                                  onClick={a.status === 'listening' ? a.stopListening : a.startListening}
                                  isDisabled={isMicDisabled}
                                  colorScheme={a.status === 'listening' ? 'red' : 'purple'}
                                  borderRadius="16px"
                                  h="44px"
                                  w="46px"
                                />
                              </ChakraTooltip>
                            </HStack>

                            {/* Secondary audio buttons */}
                            <HStack spacing={3} justify="center" px={1}>
                              <ChakraTooltip label="Silenciar respuesta" placement="top" bg="gray.850" color="white" borderRadius="8px" fontSize="xs">
                                <IconButton
                                  aria-label="Detener voz"
                                  icon={<FiVolumeX />}
                                  size="sm"
                                  variant="ghost"
                                  color="whiteAlpha.800"
                                  _hover={{ color: 'red.300', bg: 'whiteAlpha.100' }}
                                  borderRadius="full"
                                  isDisabled={a.status !== 'responding'}
                                  onClick={a.stopSpeaking}
                                />
                              </ChakraTooltip>

                              <Text fontSize="9px" color="whiteAlpha.400" textAlign="center" flex={1} fontWeight="600">
                                🎙️ Posent Voice System Premium
                              </Text>
                            </HStack>
                          </VStack>
                        </VStack>
                      </Box>
                    </MotionBox>
                  ) : (
                    <MotionBox
                      key="settings-view"
                      initial={{ opacity: 0, x: 15 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 15 }}
                      transition={{ duration: 0.25 }}
                      p={5}
                      flex={1}
                      display="flex"
                      flexDirection="column"
                      overflowY="auto"
                      className="custom-scrollbar"
                    >
                      <VStack spacing={6} align="stretch">
                        <Box>
                          <Text fontSize="lg" fontWeight="800" bgGradient="linear(to-r, purple.300, teal.300)" bgClip="text" mb={1}>
                            Ajustes del Asistente
                          </Text>
                          <Text fontSize="xs" color="whiteAlpha.600">
                            Personaliza el idioma, velocidad y tipo de voz para Posent POS.
                          </Text>
                        </Box>

                        <Divider borderColor="rgba(255,255,255,0.08)" />

                        {/* Language Selector */}
                        <VStack align="stretch" spacing={2}>
                          <Text fontSize="xs" fontWeight="700" color="purple.200" textTransform="uppercase" letterSpacing="0.5px">
                            Idioma de Voz
                          </Text>
                          <Select
                            value={a.voiceConfig.languageCode}
                            onChange={(e) => {
                              const newLang = e.target.value;
                              let defaultVoice = 'EXAVITQu4vr4xnSDxMaL'; // Bella
                              if (newLang === 'en-US') defaultVoice = '21m00Tcm4TlvDq8ikWAM'; // Rachel
                              else if (newLang === 'pt-BR') defaultVoice = 'browser';
                              a.setVoiceConfig({ languageCode: newLang, name: defaultVoice });
                            }}
                            bg="rgba(255, 255, 255, 0.04)"
                            borderColor="rgba(255, 255, 255, 0.1)"
                            color="white"
                            _hover={{ borderColor: 'purple.400' }}
                            _focus={{ borderColor: 'teal.400', boxShadow: '0 0 10px rgba(49, 151, 149, 0.2)' }}
                            borderRadius="12px"
                            fontSize="sm"
                            sx={{ option: { bg: 'gray.900', color: 'white' } }}
                          >
                            <option value="es-ES">Español (España)</option>
                            <option value="es-MX">Español (México)</option>
                            <option value="en-US">English (United States)</option>
                            <option value="pt-BR">Português (Brasil)</option>
                          </Select>
                        </VStack>

                        {/* Voice Selector */}
                        <VStack align="stretch" spacing={2}>
                          <Text fontSize="xs" fontWeight="700" color="purple.200" textTransform="uppercase" letterSpacing="0.5px">
                            Voz de ElevenLabs
                          </Text>
                          <Select
                            value={a.voiceConfig.name || 'EXAVITQu4vr4xnSDxMaL'}
                            onChange={(e) => {
                              a.setVoiceConfig(prev => ({ ...prev, name: e.target.value }));
                            }}
                            bg="rgba(255, 255, 255, 0.04)"
                            borderColor="rgba(255, 255, 255, 0.1)"
                            color="white"
                            _hover={{ borderColor: 'purple.400' }}
                            _focus={{ borderColor: 'teal.400', boxShadow: '0 0 10px rgba(49, 151, 149, 0.2)' }}
                            borderRadius="12px"
                            fontSize="sm"
                            sx={{ option: { bg: 'gray.900', color: 'white' } }}
                          >
                            {a.voiceConfig.languageCode.startsWith('es') && (
                              <>
                                <option value="EXAVITQu4vr4xnSDxMaL">Bella (ElevenLabs - Femenina Premium)</option>
                                <option value="21m00Tcm4TlvDq8ikWAM">Rachel (ElevenLabs - Voz Femenina Dulce)</option>
                                <option value="ErXwobaYiN019PkySvjV">Antoni (ElevenLabs - Voz Masculina Cálida)</option>
                                <option value="VR6A1rx1a6gmwYu8kqk8">Arnold (ElevenLabs - Voz Masculina Narrador)</option>
                              </>
                            )}
                            {a.voiceConfig.languageCode === 'en-US' && (
                              <>
                                <option value="21m00Tcm4TlvDq8ikWAM">Rachel (ElevenLabs - English Premium)</option>
                                <option value="EXAVITQu4vr4xnSDxMaL">Bella (ElevenLabs - English Premium)</option>
                                <option value="VR6A1rx1a6gmwYu8kqk8">Arnold (ElevenLabs - English Narrador)</option>
                              </>
                            )}
                            <option value="browser">Voz Nativa del Navegador (Opción Gratis)</option>
                          </Select>
                        </VStack>

                        {/* Speed slider */}
                        <VStack align="stretch" spacing={2}>
                          <HStack justify="space-between">
                            <Text fontSize="xs" fontWeight="700" color="purple.200" textTransform="uppercase" letterSpacing="0.5px">
                              Velocidad
                            </Text>
                            <Text fontSize="xs" fontWeight="bold" color="teal.300">
                              {a.audioConfig.speakingRate}x
                            </Text>
                          </HStack>
                          <Slider
                            aria-label="Velocidad de Voz"
                            min={0.5}
                            max={2.0}
                            step={0.05}
                            value={a.audioConfig.speakingRate}
                            onChange={(val) => {
                              a.setAudioConfig(prev => ({ ...prev, speakingRate: val }));
                            }}
                          >
                            <SliderTrack bg="rgba(255,255,255,0.1)">
                              <SliderFilledTrack bgGradient="linear(to-r, purple.400, teal.400)" />
                            </SliderTrack>
                            <SliderThumb boxSize={4} bg="purple.200" />
                          </Slider>
                        </VStack>

                        {/* Pitch slider */}
                        <VStack align="stretch" spacing={2}>
                          <HStack justify="space-between">
                            <Text fontSize="xs" fontWeight="700" color="purple.200" textTransform="uppercase" letterSpacing="0.5px">
                              Tono (Pitch)
                            </Text>
                            <Text fontSize="xs" fontWeight="bold" color="teal.300">
                              {a.audioConfig.pitch}x
                            </Text>
                          </HStack>
                          <Slider
                            aria-label="Tono de Voz"
                            min={0.5}
                            max={1.5}
                            step={0.05}
                            value={a.audioConfig.pitch}
                            onChange={(val) => {
                              a.setAudioConfig(prev => ({ ...prev, pitch: val }));
                            }}
                          >
                            <SliderTrack bg="rgba(255,255,255,0.1)">
                              <SliderFilledTrack bgGradient="linear(to-r, purple.400, teal.400)" />
                            </SliderTrack>
                            <SliderThumb boxSize={4} bg="purple.200" />
                          </Slider>
                        </VStack>

                        <Box pt={4}>
                          <Button
                            leftIcon={<Icon as={FiCheck} />}
                            w="full"
                            borderRadius="xl"
                            colorScheme="purple"
                            bgGradient="linear(to-r, purple.500, teal.500)"
                            onClick={() => setShowSettings(false)}
                            _hover={{ transform: 'translateY(-1px)', boxShadow: '0 4px 15px rgba(107, 70, 193, 0.3)' }}
                          >
                            Guardar y Volver
                          </Button>
                        </Box>
                      </VStack>
                    </MotionBox>
                  )}
                </AnimatePresence>
              </ModalBody>
            </ModalContent>
          </Modal>
        </ChakraProvider>
      )}
    </>
  );
};

export default GlobalVoiceAssistant;
